/**
 * Hooks optimizados con React Query para StorePage
 *
 * @description Centraliza el data fetching de la tienda con caching
 * automático, revalidación y manejo de errores
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryClient';
import { posService } from '@/services';
import { getMenusInRange } from '@/services/products/menuService';
import CategoryService from '@/services/products/categoryService';

/**
 * Hook para obtener la lista de POS públicos
 *
 * @description Caché de 1 hora - Los POS raramente cambian
 */
export function usePOSList() {
  return useQuery({
    queryKey: queryKeys.pos.lists(),
    queryFn: async () => {
      const list = await posService.getPublicPOSList();
      return list.filter((p) => p.activo !== false);
    },
    staleTime: 60 * 60 * 1000, // 1 hora
    gcTime: 2 * 60 * 60 * 1000, // 2 horas
  });
}

/**
 * Hook para obtener categorías
 *
 * @description Caché de 1 hora - Las categorías raramente cambian
 */
export function useCategories() {
  return useQuery({
    queryKey: queryKeys.categories.lists(),
    queryFn: async () => {
      const categories = await CategoryService.getAllCategories();
      const map: Record<number, string> = {};
      for (const c of categories) {
        map[c.id] = c.nombre;
      }
      return { categories, categoryById: map };
    },
    staleTime: 60 * 60 * 1000, // 1 hora
    gcTime: 2 * 60 * 60 * 1000, // 2 horas
  });
}

/**
 * Hook para obtener menús semanales
 *
 * @param startDate Fecha de inicio (YYYY-MM-DD)
 * @param endDate Fecha de fin (YYYY-MM-DD)
 * @description Caché de 30 minutos - Los menús cambian semanalmente
 */
export function useWeeklyMenus(startDate: string, endDate: string) {
  return useQuery({
    queryKey: queryKeys.menus.weekly(),
    queryFn: async () => {
      const menus = await getMenusInRange(startDate, endDate);
      return menus.sort((a, b) => a.fecha.localeCompare(b.fecha));
    },
    staleTime: 30 * 60 * 1000, // 30 minutos
    gcTime: 60 * 60 * 1000, // 1 hora
    enabled: Boolean(startDate && endDate),
  });
}

/**
 * Hook para obtener productos de un POS
 *
 * @param posId ID del POS
 * @param isApoderado Si el usuario es apoderado (filtra categoría 9)
 * @description Caché de 5 minutos - Los productos se actualizan frecuentemente
 */
export function useProductsByPOS(
  posId: number | null,
  isApoderado: boolean = false
) {
  return useQuery({
    queryKey: queryKeys.products.byPos(posId ?? 0),
    queryFn: async () => {
      if (!posId) return [];

      const posProducts = await posService.getPublicProducts(posId);

      // Filtrar: Apoderados ven TODOS los productos (incluyendo categoría 9); otros ven TODO menos categoría 9
      if (isApoderado) {
        return posProducts;
      } else {
        return posProducts.filter((p) => p.category_id !== 9);
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    enabled: Boolean(posId),
  });
}
